import * as React from 'react';
import { DefaultButton, ITheme, IStyleFunctionOrObject, concatStyleSets } from '@fluentui/react';
import { DriverErrorType } from '@fluidframework/driver-definitions';
import { OdspErrorType } from '@fluidframework/odsp-driver-definitions';
import { LoadedInfo, OWHErrorType, OfficeFluidErrorType } from '@fluidx/office-fluid-types';
import { Locale, LocaleDirection, useStringTable } from '@fluidx/utilities';
import { getStringTable } from '../../../utilities/localizationUtilities';
import { EmbedErrorInfo, EmbedMessagingStrings, getEmbedMessagingForError } from './getEmbedMessagingForError';
import { EmbedMessaging, EmbedMessagingStyles, EmbedMessagingStyleProps } from './EmbedMessaging';

export interface EmbedErrorMessagingProps {
  /**
   * Promise to the LoadedInfo. The LoadedInfo object has reference to two promises,
   * a promise to the component that's getting loaded, and a promise to the container.
   */
  loadedInfoPromise: Promise<LoadedInfo>;

  /**
   * Action handlers for different error scenarios.
   * It's implementation should fan out to handle all expected action types T.
   */
  embedMessagingActionHandlers: <
    ActionType extends 'Reload' | 'Upgrade' | 'Authorize' | 'ViewFileLocation',
    ErrorType extends OfficeFluidErrorType
  >(
    actionType: ActionType,
    errorType: ErrorType
  ) => void;

  /** Host localization */
  localization: {
    /** Host locale */
    locale: Locale;

    /** Host UI direction. If not provided, this will be determined from Host locale */
    localeDirection?: LocaleDirection;
  };

  /** Theme (provided through customization.) */
  theme?: ITheme;

  /** Call to provide customized styling */
  styles?: IStyleFunctionOrObject<EmbedMessagingStyleProps, EmbedMessagingStyles>;
}

export const EmbedErrorMessaging = React.memo((props: EmbedErrorMessagingProps) => {
  const { embedMessagingActionHandlers, loadedInfoPromise, localization, theme, styles } = props;

  const [errorInfo, setErrorInfo] = React.useState<EmbedErrorInfo>();

  // Get string table for button strings
  const stringTable = useStringTable(getStringTable, localization.locale);

  const handleLoadError = React.useCallback(async (error: unknown) => {
    const onLoadErrorInfo = await getEmbedMessagingForError(error, localization.locale);

    setErrorInfo(onLoadErrorInfo);
  }, []);

  React.useEffect(() => {
    loadedInfoPromise
      .then((loadedInfo) => {
        // Handling container and component promise rejections separately because office web host rejects them individually
        loadedInfo.container.catch((error) => {
          handleLoadError(error).catch(() => {});
        });
        loadedInfo.component.catch((error) => {
          handleLoadError(error).catch(() => {});
        });
      })
      .catch((error) => {
        // There are plans for office web host to move to single promise rejection model.
        // When that happens instead of rejecting individual container/component promise
        // office web host will reject the promise to loadedInfo. Keeping the catch block as a safety net for that change.
        handleLoadError(error).catch(() => {});
      });

    return () => {
      setErrorInfo(undefined);
    };
  }, [loadedInfoPromise]);

  // Action Handlers
  const reloadAction = () => {
    errorInfo && embedMessagingActionHandlers('Reload', errorInfo.errorType);
  };

  const upgradeAction = () => {
    errorInfo && embedMessagingActionHandlers('Upgrade', errorInfo.errorType);
  };

  const authorizeAction = () => {
    errorInfo && embedMessagingActionHandlers('Authorize', errorInfo.errorType);
  };

  const viewFileLocationAction = () => {
    errorInfo && embedMessagingActionHandlers('ViewFileLocation', errorInfo.errorType);
  };

  if (errorInfo) {
    switch (errorInfo.errorType) {
      case OWHErrorType.ContainerUpgradeRequired:
        return getEmbedMessaging(
          errorInfo.embedStrings,
          [secondaryActionButton(stringTable?.upgradeButtonText || '', upgradeAction)],
          theme,
          styles
        );

      case DriverErrorType.authorizationError:
        return getEmbedMessaging(
          errorInfo.embedStrings,
          [secondaryActionButton(stringTable?.authorizeButtonText || '', authorizeAction)],
          theme,
          styles
        );

      case OWHErrorType.ComponentNotFound:
        return getEmbedMessaging(errorInfo.embedStrings, [], theme, styles);

      case DriverErrorType.fileNotFoundOrAccessDeniedError:
        return getEmbedMessaging(errorInfo.embedStrings, [], theme, styles);

      case OdspErrorType.outOfStorageError:
        return getEmbedMessaging(
          errorInfo.embedStrings,
          [secondaryActionButton(stringTable?.viewFileLocationButtonText || '', viewFileLocationAction)],
          theme,
          styles
        );

      case DriverErrorType.throttlingError:
      default:
        return getEmbedMessaging(
          errorInfo.embedStrings,
          [secondaryActionButton(stringTable?.reloadButtonText || '', reloadAction)],
          theme,
          styles
        );
    }
  }

  return null;
});

const secondaryActionButton = (label: string, action: () => void) => (
  <DefaultButton key={`secondaryAction-${label}`} onClick={action}>
    {label}
  </DefaultButton>
);

const getEmbedMessaging = (
  errorMessage: EmbedMessagingStrings,
  actions: JSX.Element[],
  theme?: ITheme,
  styles?: IStyleFunctionOrObject<EmbedMessagingStyleProps, EmbedMessagingStyles>
): JSX.Element => {
  const content = (
    <>
      <div style={{ fontWeight: 600, display: 'inline-block' }}>
        {errorMessage.embedTitle}
        <span>&#160;&#160;</span>
      </div>
      <div style={{ display: 'inline-block' }}>{errorMessage.embedMessage}</div>
    </>
  );

  const floatingEmbedDialogStyles = concatStyleSets(getFloatingPositionStyles(theme), styles);

  return <EmbedMessaging content={content} actions={actions} theme={theme} styles={floatingEmbedDialogStyles} />;
};

/**
 * EmbedErrorMessaging is absolutely positioned within the embed space,
 * rendering over sibling elements
 * @param theme
 */
const getFloatingPositionStyles = (theme?: ITheme): EmbedMessagingStyles => {
  return {
    root: {
      position: 'absolute',
      top: 0,
      left: 0,
      margin: 0,
      padding: 0,
      width: '100%',
      height: '100%',
      zIndex: 1000, // Other elements could be floating in the component space
      backgroundColor: theme ? theme.semanticColors.bodyBackground : 'white'
    },
    content: {
      padding: 20
    },
    actions: {
      padding: '12px 20px 12px 20px'
    }
  };
};
